'use client';
import React, { useState } from 'react';

import { useIsMobile } from '@hooks/useIsMobile';

import { Label } from '@components/Common';

import { FullScreenTextarea } from './FullScreenTextarea';
import {
  MobileTextareaButton,
  MobileTextContent,
  TextareaContainer,
  TextareaField,
  TextareaWrapper,
} from './styles';

interface TextareaProps {
  id: string;
  name: string;
  label: string;
  placeholder: string;
  value: string;
  required?: boolean;
  hasError?: boolean;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** Disables the full-screen editing mode on mobile devices, showing a regular textarea instead */
  disableFullScreen?: boolean;
}

/**
 * Textarea component for listing forms
 */
export const Textarea: React.FC<TextareaProps> = ({
  id,
  name,
  label,
  placeholder,
  value,
  required = false,
  hasError = false,
  onChange,
  onBlur,
  disableFullScreen = false,
}) => {
  const isMobile = useIsMobile();
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);

  const handleMobileClick = () => {
    if (!disableFullScreen) {
      setIsFullScreenOpen(true);
    }
  };

  const handleFullScreenSave = (newValue: string) => {
    // Create a synthetic event for the onChange handler
    const syntheticEvent = {
      target: { value: newValue, name },
    } as React.ChangeEvent<HTMLTextAreaElement>;

    onChange(syntheticEvent);
  };

  const handleFullScreenClose = () => {
    setIsFullScreenOpen(false);
  };

  const shouldShowTextarea = !isMobile || disableFullScreen;

  return (
    <>
      <TextareaContainer>
        <Label htmlFor={id}>{label}</Label>
        <TextareaWrapper>
          {shouldShowTextarea ? (
            <TextareaField
              id={id}
              name={name}
              placeholder={placeholder}
              value={value}
              required={required}
              $hasError={hasError}
              onChange={onChange}
              onBlur={onBlur}
              rows={4}
            />
          ) : (
            <MobileTextareaButton type="button" onClick={handleMobileClick} $hasError={hasError}>
              <MobileTextContent>{value || placeholder}</MobileTextContent>
            </MobileTextareaButton>
          )}
        </TextareaWrapper>
      </TextareaContainer>

      {isMobile && !shouldShowTextarea && (
        <FullScreenTextarea
          isOpen={isFullScreenOpen}
          value={value}
          label={label}
          placeholder={placeholder}
          onSave={handleFullScreenSave}
          onClose={handleFullScreenClose}
        />
      )}
    </>
  );
};
